import * as React from 'react';
import { TableCell, TableRow, IconButton, Box, Grid, Typography, Collapse } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

interface ExpandableRowProps {
  row: any;
  columns: any[];
  isMobile: boolean;
  selectedPosition: string | undefined;
  onRowClick?: (id: string) => void;
  getSwapOption?: (position1Info: string[], position2Info: string[], roster: any, playerMap: any) => any;
  roster?: any;
  playerMap?: any;
}

export const ExpandableRow: React.FC<ExpandableRowProps> = ({
  row,
  columns,
  isMobile,
  selectedPosition,
  onRowClick,
  getSwapOption,
  roster,
  playerMap
}) => {
  const [open, setOpen] = React.useState(false);
  
  const prevPositionRef = React.useRef<string | null>(null);

    
    // Only log on mount and when props change
    React.useEffect(() => {
      console.log('ExpandableRow mounted or props changed');
      
      // Log prop changes
      const propKeys = Object.keys(props);
      console.log('Props:', propKeys);
  
      console.log('selectedPosition:', selectedPosition);
      
      // This effect should only run when props change
    }, [row.id, selectedPosition]);
  
  const handleClick = React.useCallback((e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent event bubbling
    console.log('Row clicked:', row.id, 'current selectedPosition:', selectedPosition);
    if (onRowClick && selectedPosition !== row.id) {
      console.log('Calling onRowClick with:', row.id);
      onRowClick(row.id);
    } else {
      console.log('Skipping onRowClick - no handler or same position');
    }
  }, [onRowClick, row.id, selectedPosition]);
  
  // Reset the ref when the row changes
  React.useEffect(() => {
    return () => {
      prevPositionRef.current = null;
    };
  }, [row.id]);

  if (!isMobile) {
    return (
      <TableRow 
        hover 
        selected={selectedPosition === row.id}
        onClick={handleClick}
        sx={{ 
          '&:hover': { cursor: onRowClick ? 'pointer' : 'default' },
          ...(selectedPosition && selectedPosition !== row.id && getSwapOption ? {
            opacity: getSwapOption(
              selectedPosition.split('-'), 
              row.id.split('-'), 
              roster, 
              playerMap
            ) === 0 ? 0.3 : 1
          } : {})
        }}
      >
        {columns.map(column => (
          <TableCell key={column.id}>
            {column.Cell ? column.Cell({ renderedCellValue: row[column.id], row }) : row[column.id]}
          </TableCell>
        ))}
      </TableRow>
    );
  }

  return (
    <React.Fragment>
      <TableRow 
        hover 
        selected={selectedPosition === row.id}
        onClick={() => setOpen(!open)}
        sx={{ '& > *': { borderBottom: 'unset' } }}
      >
        <TableCell>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setOpen(!open);
            }}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        {['position', 'player', 'projection'].map(colId => {
          const column = columns.find(c => c.id === colId || c.accessorKey === colId);
          return column ? (
            <TableCell key={colId}>
              {column.Cell ? column.Cell({ renderedCellValue: row[colId], row }) : row[colId]}
            </TableCell>
          ) : null;
        })}
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={columns.length + 1}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 1, p: 2, backgroundColor: 'background.paper', borderRadius: 1 }}>
              <Grid container spacing={2}>
                {columns
                  .filter(col => !['position', 'player', 'projection'].includes(col.id || col.accessorKey))
                  .map(column => {
                    const value = column.Cell 
                      ? column.Cell({ renderedCellValue: row[column.id || column.accessorKey], row }) 
                      : row[column.id || column.accessorKey];
                      
                    if (!value) return null;
                    
                    return (
                      <Grid item xs={6} key={column.id || column.accessorKey}>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          {column.header}
                        </Typography>
                        <Typography variant="body2">
                          {value}
                        </Typography>
                      </Grid>
                    );
                  })}
              </Grid>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
};

export default ExpandableRow;